import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {AccountService} from "../../../services/account.service";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {Router} from "@angular/router";
import {LoggedUserPersistRequest} from "../../../model/requests/logged-user-persist-request";
import {User} from "../../../model/user";
import {noSpecialCharacters, noWhitespaces} from '../../../shared/custom-validators';
import {CountryISO, PhoneNumberFormat, SearchCountryField} from 'ngx-intl-tel-input';
import * as moment from 'moment-timezone';
import {PasswordService} from "../../../services/password.service";
import {PhoneService} from "../../../services/phone.service";
import {SubscriptionData} from "../../../model/subscription-data";
import {TranslocoService} from "@ngneat/transloco";
import {AlarmsService} from "../../../services/alarms.service";
import {AlarmDefinitionSummary} from "../../../model/alarm-definition-summary";
import {AlarmSubscription} from "../../../model/alarm-subscription";
import {AuthGuard} from "../../../common/auth-guard.service";
import {AppConfig} from "../../../shared/global-variables";
import {AlarmNotificationType} from "../../../model/alarm-notification-type";
import {CommonTranslationsService} from "../../../services/common-translations.service";

@Component({
  selector: 'app-account-details',
  templateUrl: './account-details.component.html',
  styleUrls: ['./account-details.component.scss']
})
export class AccountDetailsComponent implements OnInit {

  @Output() loaded: EventEmitter<void> = new EventEmitter();

  public readonly PERMISSION_MANAGE_ALARM_SUBSCRIPTIONS: string = 'PERMISSION_MANAGE_ALARM_SUBSCRIPTIONS';

  public userForm: FormGroup;
  public persistingChanges: boolean;

  public timeZoneOptions: any[];
  public languageOptions: any[];
  public selectedLanguage: any;
  public selectedMailLanguage: any;
  public preferredCountries: any[];
  public userSubscriptions: SubscriptionData[] = new Array(10);
  public alarmSubscriptions: string[];

  public rows: number = 10;
  public first: number = 0;

  public SearchCountryField = SearchCountryField;
  public PhoneNumberFormat = PhoneNumberFormat;

  public subscriptionsLoading: boolean = true;
  public isAccountLoading: boolean = true;

  public totalUserSubscriptions: number = 0;
  public operationsTranslation: { [key: string]: string } = {};
  public notificationTypeTranslation: { [key: string]: string } = {};

  public user: User;
  public subscriptionsChanged: boolean = false;
  public formSuccess: boolean;

  private readonly DEFAULT_LANGUAGE: string = 'es';
  private readonly mode: 'create' | 'edit';


  constructor(private accountService: AccountService,
              private commonMessagesService: CommonMessagesService,
              private formBuilder: FormBuilder,
              private passwordService: PasswordService,
              private phoneService: PhoneService,
              private alarmsService: AlarmsService,
              private commonTranslationsService: CommonTranslationsService,
              private readonly i18n: TranslocoService,
              public authGuard: AuthGuard,
              private router: Router) {
    this.mode = 'edit';
    this.preferredCountries = [CountryISO.Spain];
    this.languageOptions = this.commonTranslationsService.getLanguageOptions();
    this.timeZoneOptions = moment.tz.names().map(zoneName => ({name: zoneName}));
  }

  ngOnInit(): void {
    this.loadDataAndForm();

    this.i18n.selectTranslation().subscribe(() => {
      this.buildNotificationTypeTranslation();
    });
    this.buildOperationsTranslation();
  }

  private loadDataAndForm(): void {
    this.isAccountLoading = true;
    this.subscriptionsLoading = true;

    this.accountService.getLoggedUser()
      .subscribe(response => {
        this.user = response.user;
        this.user.phone = this.phoneService.parsePhoneNumber(this.user.phone);
        this.selectedLanguage = this.languageOptions.find(lang => lang.langCode === this.user.language);

        if (this.authGuard.hasPermission(this.PERMISSION_MANAGE_ALARM_SUBSCRIPTIONS)) {
          if (this.user.mailLanguage == null) {
            this.user.mailLanguage = this.DEFAULT_LANGUAGE;
          }
          this.selectedMailLanguage = this.languageOptions.find(lang => lang.langCode === this.user.mailLanguage);
          this.alarmSubscriptions = response.subscriptions;
          this.loadSubscriptions();
        }
        this.createForm(this.user);
      }, error => {
        if (error.status === 404) {
          this.router.navigate(['/not-found']);
        }
      }).add(() => {
      this.loaded.emit();
      this.isAccountLoading = false;
    });
  }

  private loadSubscriptions(): void {
    this.alarmsService.getAlarmsDefinitionSummary(this.user.organization.organizationUuid)
      .subscribe(response => {
        const alarmsDefinition: AlarmDefinitionSummary[] = response.alarmsDefinition;
        const userSubscriptions: { [key: string]: AlarmSubscription } = this.alarmSubscriptions
          .reduce((carry, alarmDefUuid) => {
            carry[alarmDefUuid] = {
              alarmDefinitionUuid: alarmDefUuid,
              alarmNotificationType: AlarmNotificationType.INSTANTANEOUS
            };
            return carry;
          }, {});
        this.userSubscriptions = alarmsDefinition.map((alarm: AlarmDefinitionSummary) => {
          const userSubscription: SubscriptionData = {
            alarmNotificationType: null,
            ...userSubscriptions[alarm.alarmDefinitionUuid],
            alarmDefinitionUuid: alarm.alarmDefinitionUuid,
            alarmDefinitionName: alarm.name,
            deviceMeasurement: alarm.deviceMeasurement,
            operation: alarm.operation,
            value: alarm.value,
            timeWindow: alarm.timeWindow,
            organizationOwner: alarm.organizationOwner,
          }
          return userSubscription;
        });
        this.totalUserSubscriptions = this.userSubscriptions.length;
        this.subscriptionsLoading = false;
      }, error => {
        if (error.status === 404) {
          this.router.navigate(['/not-found']);
        }
      });
  }

  private createForm(user: User): void {
    this.userForm = this.formBuilder.group({
      firstName: [user.firstName, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      lastName: [user.lastName, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      username: [{
        value: user.username,
        disabled: true
      }, [Validators.required, Validators.maxLength(255), noWhitespaces, Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH)]],
      email: [user.email, [Validators.maxLength(255), Validators.required, Validators.email]],
      phone: [user.phone, [Validators.maxLength(255), Validators.required]],
      profile: [{value: user.profile.name, disabled: true}, [Validators.required, Validators.maxLength(255)]],
      timeZone: [user.timeZone, [Validators.required]],
      eCommerceUser: [user.eCommerceUser, []],
      organization: [{value: user.organization.name, disabled: true}, [Validators.required, Validators.maxLength(255)]],
      orgCode: [{value: user.organization.orgCode, disabled: true}, [Validators.required, Validators.maxLength(255)]],
      organizationType: [{
        value: user.organization.organizationType.name,
        disabled: true
      }, [Validators.required, Validators.maxLength(255)]],
      organizationParent: [{
        value: user.organization.parent ? user.organization.parent.name : '-',
        disabled: true
      }, [Validators.required, Validators.maxLength(255)]],
      password: [''],
      confirmPassword: [''],
      mailTimezone: [user.mailTimezone, [Validators.required]],
      userUuid: this.user.userUuid
    });
    this.userForm.markAsPending();
  }

  persistUserData(): void {
    this.persistingChanges = true;
    const persistData: LoggedUserPersistRequest = this.buildPersistData();

    this.accountService.editLoggedUser(persistData).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyUpdatedMessage();
      this.formSuccess = true;
      this.persistingChanges = false;
      window.location.reload();
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, this.user.username);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): LoggedUserPersistRequest {
    const formValue = this.userForm.getRawValue();
    return {
      firstName: formValue.firstName?.trim(),
      lastName: formValue.lastName?.trim(),
      email: formValue.email?.trim(),
      phone: formValue.phone ? formValue.phone.e164Number?.trim() : null,
      language: this.selectedLanguage.langCode,
      mailLanguage: this.authGuard.hasPermission(this.PERMISSION_MANAGE_ALARM_SUBSCRIPTIONS) ? this.selectedMailLanguage.langCode : null,
      mailTimezone: this.authGuard.hasPermission(this.PERMISSION_MANAGE_ALARM_SUBSCRIPTIONS) ? formValue.mailTimezone : null,
      timeZone: formValue.timeZone,
      password: formValue.password,
      tenantUUID: this.user.tenantUUID,
      subscriptions: this.buildSubscriptionsData(),
      uuid: this.user.userUuid
    };
  }

  private buildSubscriptionsData(): string[] {
    if (this.authGuard.hasPermission(this.PERMISSION_MANAGE_ALARM_SUBSCRIPTIONS)) {
      return this.userSubscriptions.filter(alarmSubscription => alarmSubscription.alarmNotificationType != null)
        .map((alarmSubscription: SubscriptionData) => alarmSubscription.alarmDefinitionUuid);
    }
    return null;
  }

  public newPasswordCheck(): void {
    this.passwordService.newPasswordCheck(this.userForm);
  }

  public generatePassword(): void {
    this.passwordService.generateAndSetPassword(this.userForm);
  }

  public changeNotificationType(userSubscription: SubscriptionData): void {
    if (userSubscription.alarmNotificationType === AlarmNotificationType.INSTANTANEOUS) {
      userSubscription.alarmNotificationType = null;
    } else {
      userSubscription.alarmNotificationType = AlarmNotificationType.INSTANTANEOUS;
    }
    this.subscriptionsChanged = true;
  }

  private buildNotificationTypeTranslation(): void {
    this.notificationTypeTranslation[AlarmNotificationType.INSTANTANEOUS] = this.i18n.translate('common.instantaneous');
    this.notificationTypeTranslation['NONE'] = this.i18n.translate('common.doNotNotify');
  }

  private buildOperationsTranslation(): void {
    this.commonTranslationsService.getOperationsTranslation().subscribe(operationsTranslation => {
      this.operationsTranslation = operationsTranslation;
    });
  }

  protected readonly AlarmNotificationType = AlarmNotificationType;

}
